import { PropsWithChildren, useEffect, useState } from 'react'
import { useGetSetting } from 'api/settings'
import chroma from 'chroma-js'
import { useThemePreference } from 'hooks/useThemePreference'
import { ThemeEnum, ThemeFont, ThemeFontEnum } from 'types/setting'

// Curated self-hosted set (shadcn/create style): the CSS chunk is imported only when the
// tenant actually selects the font, so browsers download a single family. Figtree is bundled
// statically in index.css as the default; 'system' needs no download at all.
const THEME_FONTS: Record<ThemeFont, { family: string; load?: () => Promise<unknown> }> = {
  [ThemeFontEnum.Geist]: { family: "'Geist Variable', sans-serif", load: () => import('@fontsource-variable/geist/index.css') },
  [ThemeFontEnum.Inter]: {
    family: "'Inter Variable', sans-serif",
    load: () => import('@fontsource-variable/inter/index.css'),
  },
  [ThemeFontEnum.Figtree]: { family: "'Figtree Variable', sans-serif" },
  [ThemeFontEnum.Manrope]: {
    family: "'Manrope Variable', sans-serif",
    load: () => import('@fontsource-variable/manrope/index.css'),
  },
  [ThemeFontEnum.System]: {
    family: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
}

export function generateTailwindColorPalette(baseColor: string) {
  const color = chroma(baseColor)

  return {
    50: color.brighten(2.5).hex(),
    100: color.brighten(2).hex(),
    200: color.brighten(1.5).hex(),
    300: color.brighten(1).hex(),
    400: color.brighten(0.5).hex(),
    500: color.hex(),
    600: color.darken(0.5).hex(),
    700: color.darken(1).hex(),
    800: color.darken(1.5).hex(),
    900: color.darken(1.5).hex(),
  }
}

// shadcn brand tokens: light uses the raw tenant color, dark a readable lift.
// Written as --brand* (not --primary directly) so .dark can resolve differently.
const setBrandTokens = (root: HTMLElement, prefix: string, baseColor: string) => {
  const base = chroma(baseColor)
  const foregroundFor = (c: chroma.Color) =>
    chroma.contrast(c, '#ffffff') >= chroma.contrast(c, '#0a0a0a') ? '#ffffff' : '#0a0a0a'
  const dark = base.luminance() < 0.35 ? base.brighten(1) : base

  root.style.setProperty(`--${prefix}`, base.hex())
  root.style.setProperty(`--${prefix}-foreground`, foregroundFor(base))
  root.style.setProperty(`--${prefix}-dark`, dark.hex())
  root.style.setProperty(`--${prefix}-dark-foreground`, foregroundFor(dark))
}

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const { setting } = useGetSetting()
  const themePreference = useThemePreference()
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    const initializeTheme = () => {
      if (setting) {
        // console.debug('initializeTheme', { setting })

        if (setting.primaryColor && setting.secondaryColor) {
          const root = document.documentElement

          // Only shades still read via raw var(--primary-N): ChatBubbleSparklesSvg
          // (300/400/500), ThemeToggle (500) and --brand-text (300/500).
          const primaryPalette = generateTailwindColorPalette(setting.primaryColor)
          root.style.setProperty('--primary-300', primaryPalette[300])
          root.style.setProperty('--primary-400', primaryPalette[400])
          root.style.setProperty('--primary-500', primaryPalette[500])

          // auto: default light, the sidebar ThemeToggle switches at runtime. light/dark: forced.
          root.classList.toggle('dark', themePreference === ThemeEnum.Dark)

          setBrandTokens(root, 'brand', setting.primaryColor)
          setBrandTokens(root, 'brand-2', setting.secondaryColor)

          // Whole radius scale (sm..4xl) derives from --radius via calc() in index.css.
          if (setting.themeRadius) {
            root.style.setProperty('--radius', setting.themeRadius)
          }

          const font = setting.themeFont && THEME_FONTS[setting.themeFont]
          if (font) {
            // Fire and forget: --font-sans switches immediately, glyphs swap in when the
            // lazy CSS chunk lands (font-display: swap in @fontsource).
            font.load?.().catch(console.error)
            root.style.setProperty('--font-app', font.family)
          }

          setIsInitialized(true)
        }
      }
    }

    initializeTheme()
  }, [setting, themePreference])

  return isInitialized ? <>{children}</> : null
}
